import React, { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Button, ButtonType, ButtonSize, IconName } from '@hills-design-system/components';
import InputText from '../InputText/InputText';
import ConfirmationModal from '../Modal/ConfirmationModal';
import Modal from '../Modal/Modal';
import { ModalSize } from '../Modal/Modal.types';
import { listAdmins, createAdmin, deleteAdmin, Admin } from '../../services/admin';
import { accessTokenAtom } from '../../atoms/authAtom';
import { useAdmin } from '../../hooks/useAdmin';

interface AdminManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AdminFormData {
  email: string;
  name: string;
}

const AdminManagement: React.FC<AdminManagementProps> = ({ isOpen, onClose }) => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AdminFormData>({
    email: '',
    name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const token = useAtomValue(accessTokenAtom);
  const { userEmail } = useAdmin();

  const loadAdmins = React.useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const data = await listAdmins(token);
      setAdmins(data);
    } catch (err) {
      console.error('Failed to load admins:', err);
      setError('Failed to load admins. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen && token) {
      loadAdmins();
    } else if (!isOpen) {
      // Reset search text when modal closes
      setSearchText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  const handleAddNew = () => {
    console.log('📝 Opening Add Admin form');
    console.log('Clearing form data and errors');
    setFormData({
      email: '',
      name: '',
    });
    setError(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    console.log('✖️ Closing Add Admin form');
    console.log('Resetting form state');
    setIsFormOpen(false);
    setError(null);
    setFormData({ email: '', name: '' });
  };

  const handleDelete = (adminId: string) => {
    setDeletingAdminId(adminId);
  };

  const confirmDelete = async () => {
    if (!deletingAdminId || !token) return;

    setSubmitting(true);
    setError(null);
    try {
      await deleteAdmin(token, deletingAdminId);
      await loadAdmins();
      setDeletingAdminId(null);
    } catch (err) {
      console.error('Failed to delete admin:', err);
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to delete admin. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    console.log('=== Admin Form Submission Started ===');
    console.log('Raw email value:', JSON.stringify(formData.email));
    console.log('Email length:', formData.email.length);
    console.log(
      'Email charCodes:',
      Array.from(formData.email).map((c) => c.charCodeAt(0))
    );

    setSubmitting(true);
    setError(null);

    try {
      // Trim the email BEFORE validation to remove leading/trailing spaces
      const trimmedEmail = formData.email.trim().toLowerCase();
      console.log('Trimmed email:', JSON.stringify(trimmedEmail));
      console.log('Trimmed length:', trimmedEmail.length);

      // Validate email format - allows + and other valid characters per RFC 5322
      const emailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

      console.log('Testing email against regex...');
      const isValid = emailRegex.test(trimmedEmail);
      console.log('Regex test result:', isValid);

      if (!isValid) {
        console.error('❌ Email validation FAILED');
        console.log('Email parts:', trimmedEmail.split('@'));
        setError('Please enter a valid email address');
        setSubmitting(false);
        return;
      }

      console.log('✓ Email validation passed');
      console.log('Sending email to backend:', trimmedEmail);

      await createAdmin(token, {
        email: trimmedEmail,
        name: formData.name.trim() ?? undefined,
      });

      console.log('✓ Admin created successfully');
      await loadAdmins();
      handleCloseForm();
    } catch (err) {
      console.error('❌ Failed to create admin:', err);
      const error = err as {
        response?: { data?: { message?: string }; status?: number };
        message?: string;
      };
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      const errorMessage =
        error?.response?.data?.message ?? 'Failed to create admin. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
      console.log('=== Admin Form Submission Ended ===');
    }
  };

  const filteredAdmins = admins.filter(
    (admin) =>
      admin.email.toLowerCase().includes(searchText?.toLowerCase() ?? '') ||
      admin.name?.toLowerCase().includes(searchText?.toLowerCase() ?? '')
  );

  const deletingAdmin = admins.find((a) => a.id === deletingAdminId);
  const isCurrentUser = deletingAdmin?.email === userEmail?.toLowerCase();

  const AdminListTable = () => (
    <div className="overflow-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredAdmins.map((admin) => (
            <tr key={admin.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="text-sm font-medium text-gray-900">{admin.email}</div>
                  {admin.email === userEmail?.toLowerCase() && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      You
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {admin.name ?? '—'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  onClick={() => handleDelete(admin.id)}
                  disabled={admin.email === userEmail?.toLowerCase()}
                  className={
                    admin.email === userEmail?.toLowerCase()
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-900'
                  }
                  title={
                    admin.email === userEmail?.toLowerCase()
                      ? 'Cannot delete your own admin account'
                      : 'Delete admin'
                  }
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const NoAdminFound = () => (
    <div className="text-center py-12">
      <p className="text-gray-500">No admins found</p>
    </div>
  );

  const NoAdminFilter = () => (
    <div className="text-center py-12">
      <p className="text-gray-500">No admins found matching your search</p>
    </div>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Manage Admins" size={ModalSize.Large}>
        <div className="space-y-6">
          {/* Header with Add Button */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Add or remove users who can access Admin Tools</p>
            <Button
              label="Add Admin"
              type={ButtonType.Primary}
              size={ButtonSize.Small}
              icon={IconName.Plus}
              onClick={handleAddNew}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Search Input - Always visible */}
          {!loading && admins.length > 0 && (
            <div className="mb-3">
              <input
                id="admin-search"
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by email or name..."
                className="w-full px-4 py-2.5 border border-black/20 rounded-lg focus:outline-none focus:border-blue-500 text-sm transition-colors"
              />
              {searchText && (
                <p className="text-xs text-gray-600 mt-1.5">
                  Showing {filteredAdmins.length} of {admins.length} admins
                </p>
              )}
            </div>
          )}

          {/* Admins List */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : admins.length === 0 ? (
            <NoAdminFound />
          ) : filteredAdmins.length === 0 ? (
            <NoAdminFilter />
          ) : (
            <AdminListTable />
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <Button
              label="Close"
              type={ButtonType.Secondary}
              size={ButtonSize.Medium}
              onClick={onClose}
            />
          </div>
        </div>
      </Modal>

      {/* Add Admin Form Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        title="Add New Admin"
        size={ModalSize.Medium}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <InputText
              id="admin-email"
              label="Email Address"
              value={formData.email}
              onChange={(e) => {
                console.log('📧 Email input changed:', JSON.stringify(e.target.value));
                setFormData({ ...formData, email: e.target.value });
              }}
              placeholder="admin@hills.com"
              required
              type="text"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter the email address of the user you want to grant admin access
            </p>
          </div>

          <div>
            <InputText
              id="admin-name"
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              label="Cancel"
              type={ButtonType.Secondary}
              size={ButtonSize.Medium}
              onClick={handleCloseForm}
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting || !formData.email}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Adding...' : 'Add Admin'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deletingAdminId}
        onClose={() => setDeletingAdminId(null)}
        onConfirm={confirmDelete}
        title="Delete Admin"
        message={
          isCurrentUser
            ? 'You cannot delete your own admin account.'
            : `Are you sure you want to remove admin access for "${deletingAdmin?.email}"? This action cannot be undone.`
        }
        confirmLabel={isCurrentUser ? undefined : 'Delete'}
        cancelLabel={isCurrentUser ? 'Close' : 'Cancel'}
        isDestructive={!isCurrentUser}
        isLoading={submitting}
        showConfirmButton={!isCurrentUser}
      />
    </>
  );
};

export default AdminManagement;
